import React, { useState, useEffect, useMemo, forwardRef, useImperativeHandle } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import Loader from "react-spinner-loader";
import {
  deleteRole,
  getAllPermissions,
  getThemeURL,
  getAllRoleByTrue,
} from "../../../../Services/EndPoints";
import {
  getData,
  putDataWithBody,
  postDataWithBody,
} from "../../../../Services/Services";
import Confirmpopup from "../../Common/ConfirmPopup/Confirmpopup";
import { roleTableDatacolumnsForExport } from "../../../../JsonData/RoleDetails";
import { useDispatch } from "react-redux";
import { setTableData } from "../../../../CentralStore/ExportLMSTable/dataSlice";
import { getExportedColumnsData } from "../../../../Components/UiComponents/utils";
import { useTranslation } from "react-i18next";
import {
  listStyles,
  formatDateTime,
  pill,
  EditButton,
  DeleteButton,
  ListPager,
  TextPopup,
} from "../../Common/ListCard/ListCard";

// System roles that can never be edited/deleted from the UI
const PROTECTED_ROLES = ["DEOC", "SEOC", "DashboardOnly"];

const truncate = (text, max = 60) => (text.length > max ? `${text.substring(0, max)}...` : text);

// RoleTable component used to display role table details with forwardRef to allow parent component access to internal methods
const RoleTable = forwardRef((props, ref) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const [roleData, setRoleData] = useState([]);
  const [loader, setLoader] = useState(false);
  const [permissionNames, setPermissionNames] = useState({}); // permission id -> name
  const [currentUserId, setCurrentUserId] = useState(null);
  const [searchText, setSearchText] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [deleteTarget, setDeleteTarget] = useState(null); // role awaiting delete confirmation
  const [popup, setPopup] = useState(null);               // { title, items } for description / permissions

  // Expose refreshRoleTbl method to parent component via ref
  useImperativeHandle(ref, () => ({
    refreshRoleTbl() {
      fetchAllRoles();
    },
  }));

  // Fetch roles and permissions on component mount
  useEffect(() => {
    fetchAllRoles();
    getAllUserPermission();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Fetch all permissions and create ID to name mapping
  const getAllUserPermission = async () => {
    const permissionData = await getData(getAllPermissions);
    const names = {};
    (Array.isArray(permissionData) ? permissionData : []).forEach((permission) => {
      names[permission.id] = permission.name;
    });
    setPermissionNames(names);
  };

  const getUserProfile = async () => {
    try {
      const userProfile = await getData(getThemeURL);
      if (userProfile && userProfile.length > 0) {
        const profile = userProfile[0];
        setCurrentUserId(profile.id);
        return profile;
      }
      return null;
    } catch (error) {
      console.error("Error fetching user profile:", error);
      toast.error("Failed to fetch user profile", { position: toast.POSITION.TOP_CENTER, autoClose: 2000 });
      return null;
    }
  };

  const fetchAllRoles = async () => {
    setLoader(true);
    try {
      const profile = await getUserProfile();
      if (!profile) return;

      const payload = {
        name: profile.role_name,
        // bankId: profile.bank_id || null,
      };
      const rolearr = await postDataWithBody(getAllRoleByTrue, payload, {});
      const active = (Array.isArray(rolearr) ? rolearr : []).filter((r) => r.is_deleted !== true);
      setRoleData(active.sort((a, b) => b.createdOn - a.createdOn));
    } catch (error) {
      console.error("Error fetching roles:", error);
      toast.error("Failed to fetch roles", { position: toast.POSITION.TOP_CENTER, autoClose: 2000 });
    } finally {
      setLoader(false);
    }
  };

  // ── Row helpers ──
  const permissionList = (role) =>
    (role.role_permission || [])
      .map((p) => permissionNames[p.permission_id])
      .filter(Boolean);
  const createdFor = (role) => (typeof role.is_admin === "boolean" ? (role.is_admin ? "Admin" : "User") : "NA");
  const isLocked = (role) => PROTECTED_ROLES.includes(role.name) || role.created_by !== currentUserId;

  // ── Search + pagination ──
  const filtered = useMemo(() => {
    const q = searchText.trim().toLowerCase();
    if (!q) return roleData;
    return roleData.filter((r) =>
      r.name?.toLowerCase().includes(q) ||
      createdFor(r).toLowerCase().includes(q) ||
      (r.created_by_username || "").toLowerCase().includes(q) ||
      permissionList(r).join(", ").toLowerCase().includes(q)
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [roleData, searchText, permissionNames]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const currentPage = Math.min(page, totalPages);
  const startIndex = (currentPage - 1) * pageSize;
  const pageRows = filtered.slice(startIndex, startIndex + pageSize);

  useEffect(() => { setPage(1); }, [searchText, pageSize]);

  // Keep the header "Download" export in sync with what's searched
  useEffect(() => {
    if (filtered.length > 0) {
      const data = filtered.map((role) => ({
        ...role,
        role_permission: permissionList(role).join(", "),
        is_admin: createdFor(role),
      }));
      dispatch(
        setTableData({
          columns: roleTableDatacolumnsForExport,
          tableData: getExportedColumnsData(roleTableDatacolumnsForExport, data),
        })
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtered]);

  // ── Actions ──
  const addNewHandler = () => navigate("/users-management/create-role");

  const editRole = (role) => navigate("/users-management/create-role", { state: { data: role, edit: true } });

  const deleteRoleByRoleId = async (id) => {
    try {
      const res = await putDataWithBody(deleteRole, { id }, {});
      if (res?.status === "success") {
        toast.success(res.msg, { position: toast.POSITION.TOP_CENTER, autoClose: 2000 });
        fetchAllRoles();
      } else if (res?.status === "failure") {
        toast.error(res.msg, { position: toast.POSITION.TOP_CENTER, autoClose: 6000 });
      }
    } catch (error) {
      toast.error("An error occurred while deleting the role", { position: toast.POSITION.TOP_CENTER, autoClose: 3000 });
    } finally {
      setDeleteTarget(null);
    }
  };

  const headers = ["Role Name", "Role Description", "Created For", "Created By", "Created On", "Permissions", "Actions"];
  const clickable = { cursor: "pointer" };

  return (
    <div style={listStyles.page}>
      <Loader show={loader} type="body" stack="vertical" message="Loading Data" />

      {/* Top bar */}
      <div style={{ display: "flex", gap: 10, marginBottom: 20, alignItems: "center", justifyContent: "space-between", flexWrap: "wrap" }}>
        <button type="button" onClick={addNewHandler} style={listStyles.primaryBtn}>
          + {t("createroleaddbuttonlabel")}
        </button>
        <input
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          placeholder="Search by role name, created for, created by, permission..."
          title="Search by Role Name, Created For, Created By or Permission"
          style={{ padding: "8px 12px", borderRadius: 8, border: "1px solid #cbd5e1", fontSize: 12, width: 280, maxWidth: "100%", outline: "none" }}
        />
      </div>

      <div style={listStyles.card}>

        <div style={{ overflowX: "auto" }}>
          {!loader && filtered.length === 0 ? (
            <div style={listStyles.empty}>No roles found.</div>
          ) : (
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
              <thead>
                <tr style={{ background: "linear-gradient(135deg, #0f4a45, #1a7a73)" }}>
                  {headers.map((h) => <th key={h} style={{ ...listStyles.th, color: "#fff" }}>{h}</th>)}
                </tr>
              </thead>
              <tbody>
                {pageRows.map((role, i) => {
                  const description = role.description || "";
                  const perms = permissionList(role);
                  const permText = perms.join(", ");
                  const locked = isLocked(role);
                  return (
                    <tr
                      key={role.id || i}
                      style={{ borderBottom: "1px solid #f1f5f9" }}
                      onMouseEnter={(e) => (e.currentTarget.style.background = "#f8fafc")}
                      onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
                    >
                      <td style={{ ...listStyles.td, color: "#1e293b", fontWeight: 600 }}>{role.name || "—"}</td>
                      <td style={{ ...listStyles.td, maxWidth: 220 }}>
                        {description ? (
                          <span title={description} style={description.length > 60 ? clickable : undefined}
                            onClick={() => description.length > 60 && setPopup({ title: "Full Description", items: description })}>
                            {truncate(description)}
                          </span>
                        ) : "NA"}
                      </td>
                      <td style={listStyles.td}>{pill(createdFor(role) === "Admin", "Admin", createdFor(role))}</td>
                      <td style={listStyles.td}>{role.created_by_username || "NA"}</td>
                      <td style={{ ...listStyles.td, whiteSpace: "nowrap" }}>{formatDateTime(role.createdOn) || "NA"}</td>
                      <td style={{ ...listStyles.td, maxWidth: 320 }}>
                        {perms.length ? (
                          <span title={permText} style={clickable}
                            onClick={() => setPopup({ title: `Permissions (${perms.length})`, items: perms })}>
                            {truncate(permText)}
                          </span>
                        ) : "NA"}
                      </td>
                      <td style={listStyles.td}>
                        <div style={{ display: "flex", gap: 6 }}>
                          <EditButton disabled={locked} onClick={() => !locked && editRole(role)} />
                          <DeleteButton disabled={locked} onClick={() => !locked && setDeleteTarget(role)} />
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

        <ListPager
          total={filtered.length}
          page={currentPage}
          pageSize={pageSize}
          onPage={setPage}
          onPageSize={setPageSize}
        />
      </div>

      {/* Full description / permission list */}
      {popup && <TextPopup title={popup.title} items={popup.items} onClose={() => setPopup(null)} />}

      {/* Delete confirmation (same popup as before) */}
      {deleteTarget && (
        <Confirmpopup
          className="DeltePopup"
          show={!!deleteTarget}
          title={t("roleDeletepopuptext")}
          message={t("roleDeletepopupMessage")}
          handleToggle={() => setDeleteTarget(null)}
          row={{ original: deleteTarget }}
          deleteRow={deleteRoleByRoleId}
          cancel={t("cancelbutton")}
          action={t("roleDeleteButton")}
        />
      )}
    </div>
  );
});

export default RoleTable;
